import Copy16 from '@carbon/icons-react/es/copy/16';
import Erase16 from '@carbon/icons-react/es/erase/16';
import Link16 from '@carbon/icons-react/es/link/16';
import Movement16 from '@carbon/icons-react/es/movement/16';
import OverflowMenuVertical16 from '@carbon/icons-react/es/overflow-menu--vertical/16';
import Task16 from '@carbon/icons-react/es/task/16';
import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import View16 from '@carbon/icons-react/es/view/16';
import WarningAlt16 from '@carbon/icons-react/es/warning--alt/16';
import { useBoolean } from 'ahooks';
import { Col, Dropdown, Row, Space, Tooltip } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ProjectId, TaskIdV2, UserType } from 'api/models';
import { ShiftTasksPayload } from 'api/project';
import { TaskType } from 'api/tasks';
import { ReactComponent as JiraIcon } from 'assets/svgs/jira-logo.svg';
import { ReactComponent as MilestoneTodoIcon } from 'assets/svgs/milestone.svg';
import { Button } from 'components/atoms/button';
import { CopyUrlToClipboard } from 'components/atoms/copy-to-clipboard';
import { Menu } from 'components/atoms/dropdown';
import { CustomDueDatePicker } from 'components/atoms/due-date-picker';
import { ProgressPie } from 'components/atoms/progress-pie';
import { TaskFollowersPicker } from 'components/molecules/squad-picker/followers';
import { Option } from 'components/molecules/squad-picker/types';
import { squad, squadToOptions } from 'components/molecules/squad-picker/utils';
import { MoveTaskAlertModal } from 'components/molecules/tasks/more-actions/move-task-alert-modal';
import { useTaskMoreActions } from 'components/molecules/tasks/more-actions/use-more-actions';
import { toastService } from 'components/molecules/toast';
import { IntegrationStatusTypes } from 'components/organisms/module-settings/integrations/integrations-list/authentication-types';
import { DateConflictModal } from 'components/organisms/projects/project-plan/timeline/rearrange/date-conflict-modal';
import { RearrangeTasks } from 'components/organisms/projects/project-plan/timeline/rearrange/modal';
import { Conflict } from 'components/organisms/projects/project-plan/timeline/rearrange/types';
import { RbacAddTimeEntry } from 'components/organisms/rbac/components/rbac-add-time-entry';
import { RbacProjectAssigneePicker } from 'components/organisms/rbac/components/rbac-assignee-pickers';
import {
  RbacMenuItem,
  RbacMenuItemDelete,
  RbacMoveTaskSubMenu,
} from 'components/organisms/rbac/components/rbac-menu';
import { TaskStatusPicker } from 'components/organisms/task-pane/task-detail/task-status-picker/task-status-picker';
import { DuplicateTaskDrawer } from 'components/organisms/tasks/duplicate-task/duplicate-task';
import { ClassNamePropsWithChildren } from 'components/ozone-component';
import { ofNullable } from 'core/maybe';
import { useProjectDetail } from 'data/projects/hooks';
import { Task } from 'data/tasks';
import { getTimesheetMetaState } from 'data/timesheet-meta';
import { ProjectResource, useWithAccess } from 'hooks/rbac/useWithAccess';
import { HideIfNotAllowed } from 'hooks/rbac/withAccess';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { usePartnerCompanyPermission } from 'hooks/use-partner';
import { useRemoveAsSubtaskModal } from 'hooks/use-remove-subtask-modal';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUserTimeoffLookup } from 'queries/members';
import { useShiftTasksMutation } from 'queries/project';
import { useTimesheetConfiguration } from 'queries/timesheets/configuration';
import { MoreActionsKeyHash } from '../task-detail.types';
import styles from './header.module.css';
import { ActionItem, StyledLabelForGhostInput } from './styles';

interface HeaderProps extends ClassNamePropsWithChildren {
  data: Task;
  projectId: number;
  showJiraCreate: boolean;
  canCreateJiraIssue: boolean;
  handleAssigneeChange: (
    selected: Option[],
    resolveCallback?: (value) => void,
  ) => void;
  handleDueDateChange: ([start, due]) => void;
  handleFollowersChange: (selected) => void;
  handleMarkAtRisk: () => void;
  togglePrivate: () => void;
  handleApproval: () => void;
  handleConfirmDelete: () => void;
  handleConvertToMilestone: () => void;
  handleJiraIssueCreate: () => void;
  handleJiraIssueLink: () => void;
  jiraAuth?: string;
  isProjectNonBillable: boolean;
  handleRemoveAsSubtask: () => void;
}

const StyledItem = styled(Menu.Item)`
  & span {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
`;

const StyledHeader = styled.header`
  padding-top: 0;
`;

const StyledElement = styled.div`
  & ${Button} {
    padding-left: var(--spacing-03);
    padding-right: var(--spacing-03);
    height: 40px;
  }
`;

export const Header: FC<HeaderProps> = ({
  className,
  data,
  projectId,
  showJiraCreate = false,
  canCreateJiraIssue = false,
  jiraAuth,
  isProjectNonBillable,
  handleAssigneeChange,
  handleDueDateChange,
  handleFollowersChange,
  handleConfirmDelete,
  handleMarkAtRisk,
  togglePrivate,
  handleApproval,
  handleConvertToMilestone,
  handleJiraIssueCreate,
  handleJiraIssueLink,
  handleRemoveAsSubtask,
}) => {
  const jiraAuthErrorMsg = useTranslation('jira.auth.error.message');

  const { data: projectDetail } = useProjectDetail(ProjectId(projectId));
  const {
    handleMoveTask,
    moveTaskState,
    handleMoveTaskCancel,
    isUpdateTaskPhaseLoading,
    handleMoveTaskSubmit,
  } = useTaskMoreActions({ data });
  const [timesheetsEnabled] = useAccountFeature(AccountFeature.Timesheets);
  const { canPartnerTrackTime } = usePartnerCompanyPermission();
  const { user } = useUser();
  const { data: configuration } = useTimesheetConfiguration();
  const { data: timesheetMeta } = useSelector(getTimesheetMetaState);
  const [
    isDuplicateTaskDrawerVisible,
    { setTrue: openDuplicateTaskDrawer, setFalse: closeDuplicateTaskDrawer },
  ] = useBoolean(false);
  const translatedDeleteConfirmTitleText = useTranslation(
    'task.delete.confirm_title',
  );
  const translatedDeleteConfirmMessageText = useTranslation(
    'task.delete.confirm_message',
  );
  const translatedClickToCopyText = useTranslation('task.click_to_copy');
  const translateRemoveAsSubtaskTitle = useTranslation(
    'projects.remove.as.subtask_title',
  );

  const translateRemoveAsSubtaskBody = useTranslation(
    'projects.remove.as.subtask_body',
  );

  const [showRearrangeModal, setRerrangeModalVisibility] = useState(false);
  const [showTaskConflict, setShowTaskConflict] = useState(false);
  const [loading, setLoading] = useState(false);
  const { mutateAsync } = useShiftTasksMutation(projectId);

  const translate = useTranslate();

  const [actions, setActions] = useState<ShiftTasksPayload>({
    updatedPhases: [],
    updatedTasks: [],
  });

  const [conflicts, setConflicts] = useState<Conflict[]>([]);

  const onClose = useCallback(() => {
    setRerrangeModalVisibility(false);
    setShowTaskConflict(false);
    setConflicts([]);
    setActions({
      updatedPhases: [],
      updatedTasks: [],
    });
  }, []);

  const rearrangeTasks = useCallback(
    async (updatedActions) => {
      try {
        setLoading(true);
        const response = await mutateAsync(updatedActions);

        if (response) {
          setLoading(false);
          onClose();
          toastService({
            title: translate('success'),
            message: translate('tasks.rearrange_success'),
          }).success();
        }
      } catch {
        toastService({
          title: translate('action_failed'),
          message: translate('tasks.rearrange_failed_body'),
        }).error();
      }
    },
    [mutateAsync, onClose, translate],
  );

  const onRearrange = useCallback(() => {
    setRerrangeModalVisibility(true);
  }, []);

  const isTimesheetConfigEnabledForTask = useCallback(() => {
    if (data.type === TaskType.Milestone) {
      if (configuration?.enabledSourceTypes?.MILESTONE) {
        return true;
      }

      return false;
    }
    if (configuration?.enabledSourceTypes?.TASK) {
      return true;
    }

    return false;
  }, [
    configuration?.enabledSourceTypes?.MILESTONE,
    configuration?.enabledSourceTypes?.TASK,
    data.type,
  ]);

  const DeleteConfirmation = useDeleteConfirmModal({
    title: translatedDeleteConfirmTitleText,
    content: translatedDeleteConfirmMessageText,
    onOk: () => handleConfirmDelete(),
  });

  const RemoveAsSubtaskModal = useRemoveAsSubtaskModal({
    title: translateRemoveAsSubtaskTitle,
    content: translateRemoveAsSubtaskBody,
    onOk: () => handleRemoveAsSubtask(),
  });

  const isMilestone = data.type === TaskType.Milestone;

  const phaseDetail =
    projectDetail?.projectPhases?.filter(
      (phase) => phase.projectPhaseId === data.projectPhase?.projectPhaseId,
    ) || [];

  const isPhasePrivate = phaseDetail?.[0]?.private ?? false;
  const isParentPrivate = data.parentTaskObject?.private ?? false;

  // when say a task/subtask is shared but the phase/parent-task is private, we need the option to make it private but not shared.
  const showPrivacyMenuItem =
    !(isPhasePrivate || isParentPrivate) || !data.private;

  const projectResource = useMemo(
    () => (projectDetail ? ProjectResource(projectDetail) : undefined),
    [projectDetail],
  );
  const { hasAccessToResource } = useWithAccess();

  const hasAccessToUpdateDueDate = hasAccessToResource({
    rule: 'TASK_DATES:WRITE',
    resourceData: projectResource,
  });

  const hasAccessToUpdateStatus = hasAccessToResource({
    rule: 'TASK_STATUS:WRITE',
    resourceData: projectResource,
  });

  const hasAccessToCreateOrLinkJiraIssue = hasAccessToResource({
    rule: 'TASK:WRITE',
    resourceData: projectResource,
  });

  const MoreActionsMenu = () => (
    <Menu>
      <Menu.Header>
        <FormattedMessage id="more_actions" />
      </Menu.Header>

      <RbacMenuItem
        data-cy="task_more_options.milestone"
        key={MoreActionsKeyHash.ConvertToMilestone}
        icon={
          isMilestone ? (
            <ProgressPie progress={0} type="default" />
          ) : (
            <MilestoneTodoIcon />
          )
        }
        onClick={handleConvertToMilestone}
        rule="TASK_TYPE:WRITE"
        resourceData={projectResource}
      >
        {isMilestone ? (
          <FormattedMessage id="task.convert_to_task" />
        ) : (
          <FormattedMessage id="task.convert_to_milestone" />
        )}
      </RbacMenuItem>

      <RbacMenuItem
        data-cy="task_more_options.at_risk"
        key={MoreActionsKeyHash.MarkAtRisk}
        icon={<WarningAlt16 />}
        onClick={handleMarkAtRisk}
        rule="TASK_AT_RISK:WRITE"
        resourceData={projectResource}
      >
        {data && data.atRisk ? (
          <FormattedMessage id="task.remove_at_risk" />
        ) : (
          <FormattedMessage id="task.mark_at_risk" />
        )}
      </RbacMenuItem>
      {showPrivacyMenuItem && (
        <RbacMenuItem
          data-cy="task_more_options.change_privacy"
          key={MoreActionsKeyHash.ChangePrivacy}
          icon={data.private ? <View16 /> : <ViewOff16 />}
          onClick={togglePrivate}
          rule="TASK_PRIVACY:WRITE"
          resourceData={projectResource}
        >
          {data && data.private ? (
            <FormattedMessage id="task.mark_shared" />
          ) : (
            <FormattedMessage id="task.mark_private" />
          )}
        </RbacMenuItem>
      )}
      {!data.parentTask ? (
        <RbacMoveTaskSubMenu
          rule="TASK_UPDATE_PHASE:WRITE"
          resourceData={projectResource}
          phases={projectDetail?.projectPhases?.filter(
            ({ projectPhaseId }) =>
              projectPhaseId !== data.projectPhase?.projectPhaseId,
          )}
          onClick={handleMoveTask}
        />
      ) : null}
      {!data.formInstanceId && data.parentTask && (
        <Menu.Item
          icon={<Erase16 />}
          onClick={() => {
            RemoveAsSubtaskModal();
          }}
        >
          <FormattedMessage id="projects.remove.as.subtask_title" />
        </Menu.Item>
      )}

      <RbacMenuItem
        icon={<Movement16 />}
        key="move"
        onClick={onRearrange}
        data-cy="project-task-drawer-more-actions-rearrange"
        rule="TASK_UPDATE_PHASE:WRITE"
        resourceData={projectResource}
      >
        <FormattedMessage id="templates.rearrange_tasks.title" />
      </RbacMenuItem>

      <RbacMenuItem
        data-cy="task_more_options.request_approval"
        key={MoreActionsKeyHash.RequestApproval}
        icon={<Task16 />}
        onClick={handleApproval}
        rule="TASK_REQUEST_APPROVAL:WRITE"
        resourceData={projectResource}
      >
        <FormattedMessage id="task.request_approval" />
      </RbacMenuItem>
      {!data.formInstanceId ? (
        <>
          {data.parentTask ? (
            <RbacMenuItem
              data-cy="task_more_options.duplicateTask"
              icon={<Copy16 />}
              onClick={openDuplicateTaskDrawer}
              rule="TASK_DUPLICATE:WRITE"
              resourceData={projectResource}
            >
              <FormattedMessage id="task.duplicate_sub_task" />
            </RbacMenuItem>
          ) : (
            <RbacMenuItem
              data-cy="task_more_options.duplicateTask"
              icon={<Copy16 />}
              onClick={openDuplicateTaskDrawer}
              rule="TASK_DUPLICATE:WRITE"
              resourceData={projectResource}
            >
              <FormattedMessage id="task.duplicate_task" />
            </RbacMenuItem>
          )}
        </>
      ) : null}
      {canCreateJiraIssue && hasAccessToCreateOrLinkJiraIssue && (
        <>
          <Menu.Divider />
          <StyledItem
            data-cy="task_more_options.jira"
            key={MoreActionsKeyHash.CreateJiraIssue}
            icon={
              <JiraIcon
                style={
                  jiraAuth === IntegrationStatusTypes.Expired
                    ? { opacity: '0.5' }
                    : {}
                }
              />
            }
            onClick={handleJiraIssueCreate}
            disabled={
              jiraAuth === IntegrationStatusTypes.Expired || !showJiraCreate
            }
          >
            <FormattedMessage id="task.create_jira_issue" />
            {jiraAuth === IntegrationStatusTypes.Expired && (
              <Tooltip
                align={{ offset: [30] }}
                title={jiraAuthErrorMsg}
                placement="right"
              >
                <WarningAlt16 color="var(--orange-40)" />
              </Tooltip>
            )}
          </StyledItem>
          <StyledItem
            key={MoreActionsKeyHash.LinkJiraIssue}
            icon={
              <Link16
                style={
                  jiraAuth === IntegrationStatusTypes.Expired
                    ? { color: 'var(--blue-60)', opacity: '0.5' }
                    : { color: 'var(--blue-60)' }
                }
              />
            }
            onClick={handleJiraIssueLink}
            disabled={
              jiraAuth === IntegrationStatusTypes.Expired || !showJiraCreate
            }
          >
            <FormattedMessage id="task.link_jira_issue" />
            {jiraAuth === IntegrationStatusTypes.Expired && (
              <Tooltip
                align={{ offset: [30] }}
                title={jiraAuthErrorMsg}
                placement="right"
              >
                <WarningAlt16 color="var(--orange-40)" />
              </Tooltip>
            )}
          </StyledItem>
        </>
      )}

      <>
        <Menu.Divider />
        <RbacMenuItemDelete
          rule={isMilestone ? 'MILESTONE:DELETE' : 'TASK:DELETE'}
          resourceData={projectResource}
          data-cy="task-more-actions__delete"
          key={MoreActionsKeyHash.Delete}
          onClick={() => {
            DeleteConfirmation();
          }}
        />
      </>
    </Menu>
  );

  const { data: userLookup } = useUserTimeoffLookup();

  return (
    <>
      {isDuplicateTaskDrawerVisible ? (
        <DuplicateTaskDrawer
          task={data}
          closeHandler={closeDuplicateTaskDrawer}
          onCancel={closeDuplicateTaskDrawer}
        />
      ) : null}
      <StyledHeader className={`${styles.header} ${className}`}>
        <Row align="middle" justify="space-between" className={styles.actions}>
          <Col span={19}>
            <Space align="center" size={0}>
              <ActionItem>
                <StyledLabelForGhostInput>
                  <FormattedMessage id="assign_to" />
                </StyledLabelForGhostInput>
                <StyledElement>
                  <RbacProjectAssigneePicker
                    projectId={ProjectId(projectId)}
                    isPrivate={data.private}
                    startDate={data.startDate}
                    dueDate={data.dueDate}
                    value={squadToOptions(squad(data.assignee, userLookup))}
                    onDone={handleAssigneeChange}
                    onSave={handleAssigneeChange}
                    rule="TASK_ASSIGNEE:WRITE"
                    resourceData={projectResource}
                    propsIfNoAccess={{
                      isDisabled: true,
                    }}
                  />
                </StyledElement>
              </ActionItem>
              <div className={styles.actions__item}>
                <StyledLabelForGhostInput htmlFor="due_date">
                  <FormattedMessage id="due_date" />
                </StyledLabelForGhostInput>
                <StyledElement>
                  <CustomDueDatePicker
                    id="due_date"
                    size="small"
                    startDate={data.startDate}
                    dueDate={data.dueDate}
                    onDateChange={handleDueDateChange}
                    disabled={!hasAccessToUpdateDueDate}
                  />
                </StyledElement>
              </div>
              <div className={styles.actions__item}>
                <StyledElement>
                  <TaskStatusPicker
                    data={data}
                    disabled={!hasAccessToUpdateStatus}
                  />
                </StyledElement>
              </div>
            </Space>
          </Col>
          <Col span={5}>
            <Row justify="end">
              {timesheetsEnabled &&
              (user.userType === UserType.Native || canPartnerTrackTime) &&
              !timesheetMeta?.excludedFromTimesheet &&
              isTimesheetConfigEnabledForTask() ? (
                <RbacAddTimeEntry
                  taskId={TaskIdV2(data.taskId)}
                  defaultBillable={
                    isProjectNonBillable ? false : data.defaultTimeEntryBillable
                  }
                  defaultCategory={ofNullable(
                    data.defaultTimeEntryCategory ?? null,
                  )}
                  projectId={projectId}
                  projectDetail={projectDetail}
                  billableDisabled={isProjectNonBillable}
                  rule="TIMESHEET:WRITE"
                />
              ) : null}
              <CopyUrlToClipboard popoverText={translatedClickToCopyText} />
              <HideIfNotAllowed
                rule="TASK:WRITE"
                resourceData={projectResource}
              >
                <TaskFollowersPicker
                  isPrivate={data.private}
                  value={squadToOptions(squad(data.followers))}
                  onChange={handleFollowersChange}
                  projectId={ProjectId(projectId)}
                />
              </HideIfNotAllowed>

              <Dropdown
                overlay={MoreActionsMenu}
                trigger={['click']}
                placement="bottomRight"
              >
                <Tooltip title={<FormattedMessage id="task.more_actions" />}>
                  <Button
                    type="text"
                    icon={<OverflowMenuVertical16 />}
                    size="small"
                    data-cy="task-more-actions"
                  />
                </Tooltip>
              </Dropdown>
            </Row>
          </Col>
        </Row>
        {moveTaskState.projectPhase ? (
          <MoveTaskAlertModal
            task={data}
            projectPhase={moveTaskState.projectPhase}
            onCancel={handleMoveTaskCancel}
            confirmLoading={isUpdateTaskPhaseLoading}
            onSubmit={handleMoveTaskSubmit}
          />
        ) : null}

        {showRearrangeModal && (
          <RearrangeTasks
            visible={showRearrangeModal}
            currentTaskId={data.taskId}
            projectId={ProjectId(projectId)}
            onClose={onClose}
            loading={loading}
            actions={actions}
            setActions={setActions}
            rearrangeTasks={rearrangeTasks}
            next={() => {
              setRerrangeModalVisibility(false);
              setShowTaskConflict(true);
            }}
            conflicts={conflicts}
            setConflicts={setConflicts}
          />
        )}

        {showTaskConflict && (
          <DateConflictModal
            visible={showTaskConflict}
            onClose={() => {
              onClose();
            }}
            rearrangeTasks={rearrangeTasks}
            actions={actions}
            setActions={setActions}
            loading={loading}
            dataSource={conflicts}
            setDataSource={setConflicts}
          />
        )}
      </StyledHeader>
    </>
  );
};
